/**
 * Phone number validation utilities for Georgian phone numbers
 * 
 * Valid Georgian formats:
 * - 5XXXXXXXX (9 digits starting with 5)
 * - +995 5XXXXXXXX (with country code)
 * - 995 5XXXXXXXX (with country code, no +)
 * 
 * Supports multiple phones separated by " / "
 */

/**
 * Validates a single Georgian phone number
 */
export function isValidGeorgianPhone(phone: string): boolean {
  if (!phone || !phone.trim()) return false;
  
  // Remove spaces, dashes, parentheses for validation
  const cleaned = phone.replace(/[\s\-()]/g, "");
  
  // Valid patterns:
  // - 5XXXXXXXX (9 digits starting with 5)
  // - +9955XXXXXXXX or 9955XXXXXXXX (with country code)
  return /^(\+?995)?5\d{8}$/.test(cleaned);
}

/**
 * Validates all phone numbers in a string (separated by " / ")
 * Returns validation result for each phone
 */
export function validatePhoneNumbers(phoneString: string | null | undefined): Array<{
  phone: string;
  isValid: boolean;
}> {
  if (!phoneString || !phoneString.trim()) return [];
  
  // Split by " / " separator
  const phones = phoneString.split(/\s*\/\s*/);
  
  return phones.map(phone => ({
    phone: phone.trim(),
    isValid: isValidGeorgianPhone(phone.trim())
  }));
}

/**
 * Checks if phone field contains any invalid numbers
 */
export function hasPhoneValidationIssue(phone: string | null | undefined): boolean {
  if (!phone || !phone.trim()) return false;
  
  const validationResults = validatePhoneNumbers(phone);
  return validationResults.some(result => !result.isValid);
}

/**
 * Get only the invalid phones from a phone string
 */
export function getInvalidPhones(phone: string | null | undefined): string[] {
  if (!phone || !phone.trim()) return [];
  
  return validatePhoneNumbers(phone)
    .filter(result => !result.isValid)
    .map(result => result.phone);
}
